/**
 * Authentication and Token Validation Utilities
 */

/**
 * Checks if a JWT token string is structurally valid and not expired.
 * @param {string} token
 * @returns {boolean}
 */
export const isTokenValid = (token) => {
  if (!token || typeof token !== 'string') return false;
  const parts = token.trim().split('.');
  if (parts.length !== 3) return false;

  try {
    // Decode base64url payload
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    const parsed = JSON.parse(jsonPayload);

    // If token has expiration timestamp, verify it is in the future
    if (parsed && typeof parsed.exp === 'number') {
      const currentTimeInSeconds = Math.floor(Date.now() / 1000);
      if (parsed.exp <= currentTimeInSeconds) {
        return false; // Token has expired
      }
    }
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Retrieves valid authenticated user from localStorage.
 * Automatically clears stale/tokenless/expired sessions.
 * @returns {object|null}
 */
export const getValidStoredUser = () => {
  try {
    const raw = localStorage.getItem('userInfo');
    if (!raw) return null;

    const user = JSON.parse(raw);
    if (!user || typeof user !== 'object') {
      localStorage.removeItem('userInfo');
      return null;
    }

    // Token must exist and be valid
    if (!user.token || !isTokenValid(user.token)) {
      localStorage.removeItem('userInfo');
      return null;
    }

    return user;
  } catch (e) {
    localStorage.removeItem('userInfo');
    return null;
  }
};

/**
 * Normalizes role string or role ID to canonical lowercase alphanumeric key
 * @param {string|number} role
 * @param {string|number} roleId
 * @returns {string}
 */
export const getNormalizedRoleKey = (role, roleId) => {
  const rId = parseInt(roleId, 10);
  if (rId === 1) return 'superadmin';
  if (rId === 2) return 'superdistributor';
  if (rId === 3) return 'masterdistributor';
  if (rId === 4) return 'distributor';
  if (rId === 5) return 'retailer';
  if (rId === 6) return 'operations';

  const raw = String(role || '').toLowerCase().trim().replace(/[\s-_]+/g, '');
  if (raw.includes('superadmin') || raw.includes('admin')) return 'superadmin';
  if (raw.includes('superdistributor')) return 'superdistributor';
  if (raw.includes('masterdistributor')) return 'masterdistributor';
  if (raw.includes('distributor')) return 'distributor';
  if (raw.includes('retail')) return 'retailer';
  if (raw.includes('report')) return 'reports';
  return raw;
};

/**
 * Validates if the user's role is included in allowedRoles
 * @param {object} userInfo
 * @param {string[]} allowedRoles
 * @returns {boolean}
 */
export const isUserRoleAllowed = (userInfo, allowedRoles) => {
  if (!allowedRoles || allowedRoles.length === 0) return true;
  if (!userInfo) return false;

  const userRoleKey = getNormalizedRoleKey(userInfo.role, userInfo.role_id);
  const normalizedAllowed = allowedRoles.map((r) =>
    String(r).toLowerCase().trim().replace(/[\s-_]+/g, '')
  );

  return normalizedAllowed.includes(userRoleKey);
};
